import {
  AccessControlItem,
  AccessControlKind,
  AccessControlUser,
} from '../services/accessControlService';
import { matchesSearch } from './biCatalog';

// Mesma regra da API para códigos novos ou alterados.
export const IDENTIFICATION_PATTERN = /^[a-z0-9]+(?:[_.-][a-z0-9]+)*$/;
export const IDENTIFICATION_MAX = 80;

export const KIND_LABEL: Record<
  AccessControlKind,
  { one: string; many: string; title: string; titleMany: string }
> = {
  permission: {
    one: 'permissão',
    many: 'permissões',
    title: 'Permissão',
    titleMany: 'Permissões',
  },
  role: {
    one: 'função',
    many: 'funções',
    title: 'Função',
    titleMany: 'Funções',
  },
};

export function normalizeIdentification(value?: string | null): string {
  return (value || '').trim().toLowerCase();
}

// "BI Vendas por Filial" → "bi_vendas_por_filial".
export function toIdentification(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, IDENTIFICATION_MAX)
    .replace(/_+$/g, '');
}

export function findDuplicate(
  items: Pick<AccessControlItem, 'id' | 'name' | 'identification'>[],
  identification: string,
  ignoreId?: string,
) {
  const wanted = normalizeIdentification(identification);

  return items.find(
    item =>
      item.id !== ignoreId &&
      normalizeIdentification(item.identification) === wanted,
  );
}

export function identificationError(
  value: string,
  items: Pick<AccessControlItem, 'id' | 'name' | 'identification'>[],
  ignoreId?: string,
): string | undefined {
  const text = value.trim();

  if (!text) {
    return 'Informe a identificação.';
  }

  if (text.length > IDENTIFICATION_MAX || !IDENTIFICATION_PATTERN.test(text)) {
    return 'Use só letras minúsculas sem acento, números e "_" (ex.: bi_vendas_lojas).';
  }

  const duplicate = findDuplicate(items, text, ignoreId);

  return duplicate
    ? `Já existe com este código: ${duplicate.name}.`
    : undefined;
}

export function diffIds(
  initial: string[],
  selected: string[],
): { added: string[]; removed: string[] } {
  return {
    added: selected.filter(id => !initial.includes(id)),
    removed: initial.filter(id => !selected.includes(id)),
  };
}

export type ItemIssue = 'duplicate' | 'format' | 'no-users';

// Pontos de atenção no cadastro, mostrados como etiquetas na lista.
export function itemIssues(
  item: AccessControlItem,
  items: AccessControlItem[],
): ItemIssue[] {
  const issues: ItemIssue[] = [];

  if (findDuplicate(items, item.identification, item.id)) {
    issues.push('duplicate');
  }

  if (!IDENTIFICATION_PATTERN.test(item.identification || '')) {
    issues.push('format');
  }

  if (item.total_accesses === 0) {
    issues.push('no-users');
  }

  return issues;
}

export type ItemFilter = 'all' | 'with-users' | 'no-users' | 'issues';

export function filterItems(
  items: AccessControlItem[],
  search: string,
  filter: ItemFilter,
): AccessControlItem[] {
  return items.filter(item => {
    if (filter === 'with-users' && item.total_accesses === 0) {
      return false;
    }

    if (filter === 'no-users' && item.total_accesses > 0) {
      return false;
    }

    if (
      filter === 'issues' &&
      itemIssues(item, items).filter(issue => issue !== 'no-users').length === 0
    ) {
      return false;
    }

    return matchesSearch(
      `${item.name} ${item.identification} ${item.description || ''}`,
      search,
    );
  });
}

export interface UserOption {
  value: string;
  label: string;
}

// Com o setor no texto, buscar "contabilidade" encontra o setor inteiro.
export function userLabel(user: AccessControlUser, withSector = false): string {
  const parts = [user.name, user.nickname].filter(Boolean).join(' · ');
  const text = withSector && user.sector ? `${parts} — ${user.sector}` : parts;

  return user.status ? text : `${text} (inativo)`;
}

export function toUserOptions(
  users: AccessControlUser[],
  withSector = false,
): UserOption[] {
  return users.map(user => ({
    value: user.id,
    label: userLabel(user, withSector),
  }));
}

// Busca do seletor sem diferenciar acento e maiúsculas.
export function filterOptions<T extends { label: string }>(
  options: T[],
  filter: string,
): T[] {
  return filter.trim()
    ? options.filter(option => matchesSearch(option.label, filter))
    : options;
}

export function plural(total: number, one: string, many: string): string {
  return `${total} ${total === 1 ? one : many}`;
}
